import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";

const fmt = (n, d = 2) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });

// Mirrors `agtronBucket` in /pages/Cupping.jsx so the PDF labels the
// roast level (Light/Medium/etc.) the same way the on-screen sample
// card does.
function agtronInfoFor(n) {
  if (n == null || n === "" || isNaN(parseFloat(n))) return null;
  const v = parseFloat(n);
  if (v >= 80 && v <= 100) return { label: "Light Roast" };
  if (v >= 66 && v <= 79)  return { label: "Medium-Light" };
  if (v >= 50 && v <= 65)  return { label: "Medium Roast" };
  if (v >= 41 && v <= 49)  return { label: "Medium-Dark" };
  if (v >= 25 && v <= 40)  return { label: "Dark Roast" };
  return { label: "Out of range" };
}

const SCORE_KEYS = [
  ["fragrance",  "Fragrance"],
  ["flavour",    "Flavour"],
  ["aftertaste", "Aftertaste"],
  ["acidity",    "Acidity"],
  ["body",       "Body"],
  ["balance",    "Balance"],
  ["sweetness",  "Sweetness"],
  ["clean_cup",  "Clean cup"],
  ["uniformity", "Uniformity"],
  ["overall",    "Overall"],
];

// Filled session summary — one or more samples per page, with all scores +
// AI reports. Designed for browser Print → Save as PDF (A4 portrait).
export default function CuppingReport() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/cuppings/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load"));
  }, [id]);

  if (error) return <p style={{ padding: 24, color: "#b00" }}>{error}</p>;
  if (!data) return null;

  const samples = data.samples || [];
  const cupperSet = new Set();
  samples.forEach((s) => (s.scores || []).forEach((sc) => cupperSet.add(sc.user_name)));
  const sessionAvg = samples.length
    ? samples.reduce((acc, s) => acc + (s.avg_overall || 0), 0) / samples.length
    : 0;

  return (
    <div className="report-root" style={{ background: "white", color: "#1C1816", padding: 24, minHeight: "100vh", fontFamily: "Manrope, sans-serif" }} data-testid="cupping-report">
      <style>{`
        @page { size: A4 portrait; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
          .report-root { padding: 0 !important; }
          .sample-block { page-break-inside: avoid; }
          .ai-report { page-break-inside: avoid; }
        }
        h1, h2, h3 { font-family: 'Cabinet Grotesk', Manrope, sans-serif; }
        table.scores { border-collapse: collapse; width: 100%; font-size: 10px; }
        table.scores th, table.scores td { border: 1px solid #ccc; padding: 4px 6px; text-align: right; }
        table.scores th { background: #f0ece4; text-align: center; font-weight: 600; }
        table.scores td.cupper { text-align: left; font-weight: 600; }
        table.scores tr.avg-row { background: #fdf6f0; font-weight: 700; }
      `}</style>

      <div className="no-print" style={{ marginBottom: 18, display: "flex", gap: 12 }}>
        <button onClick={() => window.print()} data-testid="report-print"
          style={{ padding: "8px 16px", background: "#8C583E", color: "white", border: "none", borderRadius: 4, fontWeight: 600, cursor: "pointer" }}>
          Print / Save as PDF
        </button>
        <button onClick={() => window.close()}
          style={{ padding: "8px 16px", background: "white", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}>
          Close
        </button>
      </div>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16, borderBottom: "2px solid #1C1816", paddingBottom: 8 }}>
        <div>
          <p style={{ fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", margin: 0, color: "#736B63" }}>Brothers Coffee Roastery — Cupping Session Report</p>
          <h1 style={{ margin: "4px 0 0 0", fontSize: 22 }}>{data.name}</h1>
          {data.notes && <p style={{ margin: "4px 0 0 0", fontSize: 11, color: "#5C544C" }}>{data.notes}</p>}
        </div>
        <div style={{ textAlign: "right", fontSize: 11 }}>
          <p style={{ margin: 0 }}><strong>Date:</strong> {data.cupping_date}</p>
          <p style={{ margin: 0 }}><strong>Cuppers:</strong> {[...cupperSet].join(", ") || "—"}</p>
          <p style={{ margin: 0 }}><strong>Samples:</strong> {samples.length}</p>
        </div>
      </div>

      {/* Session summary band */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 18 }}>
        <Kpi label="Samples" value={samples.length} />
        <Kpi label="Cuppers" value={cupperSet.size} />
        <Kpi label="Session avg overall" value={sessionAvg ? fmt(sessionAvg) : "—"} />
        <Kpi label="Highest sample" value={samples.length ? topSample(samples) : "—"} />
      </div>

      {samples.length === 0 && (
        <p style={{ fontSize: 12, color: "#736B63", padding: 24, textAlign: "center" }}>
          No samples in this session.
        </p>
      )}

      {samples.map((s, i) => (
        <div key={s.sample_id} className="sample-block" style={{ marginBottom: 22, border: "1px solid #d8d2c8", borderRadius: 6, padding: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "1px solid #ddd", paddingBottom: 6, marginBottom: 8 }}>
            <div>
              <p style={{ fontSize: 9, letterSpacing: 1.2, textTransform: "uppercase", margin: 0, color: "#736B63" }}>
                Sample {String.fromCharCode(65 + i)}
                {s.roast_number && ` · Roast #${s.roast_number}`}
              </p>
              <h3 style={{ margin: "2px 0 0 0", fontSize: 16 }}>
                {s.label}
                {s.roast_origin_name && <span style={{ color: "#736B63", fontWeight: 400 }}> · {s.roast_origin_name}</span>}
              </h3>
              {s.roast_date && (
                <p style={{ fontSize: 10, color: "#736B63", margin: "2px 0 0 0", fontFamily: "JetBrains Mono, monospace" }}>
                  Roasted {new Date(s.roast_date).toLocaleDateString("en-ZA")}
                  {s.roast_loss_pct != null && ` · loss ${fmt(s.roast_loss_pct, 1)}%`}
                </p>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={{ fontSize: 9, letterSpacing: 1.2, textTransform: "uppercase", margin: 0, color: "#736B63" }}>Avg overall</p>
              <p style={{ fontSize: 24, fontWeight: 700, margin: 0, fontVariantNumeric: "tabular-nums" }}>
                {s.avg_overall ? fmt(s.avg_overall) : "—"}
              </p>
            </div>
          </div>

          {/* TDS / Extraction / Agtron band — now includes the Agtron
              numeric reading + auto roast-level label so the PDF
              mirrors what's in the on-screen sample card. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 6, marginBottom: 10 }}>
            <Mini label="TDS %"        value={s.tds_pct != null ? fmt(s.tds_pct) : "—"} />
            <Mini label="Yield (g)"    value={s.yield_g != null ? fmt(s.yield_g, 1) : "—"} />
            <Mini label="Dose (g)"     value={s.dose_g != null ? fmt(s.dose_g, 1) : "—"} />
            <Mini label="Extraction %" value={s.extraction_yield_pct != null ? fmt(s.extraction_yield_pct) : "—"} accent />
            <Mini label="Moisture %"   value={s.moisture != null ? fmt(s.moisture, 1) : "—"} />
            <Mini
              label={agtronInfoFor(s.agtron) ? `Agtron · ${agtronInfoFor(s.agtron).label}` : "Agtron"}
              value={s.agtron != null ? fmt(s.agtron, 0) : "—"}
            />
          </div>

          {/* Sample-level notes (separate from per-cupper tasting notes —
              this is what the operator typed into the sample editor). */}
          {s.notes && (
            <div style={{ marginBottom: 10, padding: "6px 8px", background: "#fbf8f3", border: "1px solid #e6dfd2", borderRadius: 4 }}>
              <p style={{ fontSize: 9, letterSpacing: 1, textTransform: "uppercase", color: "#736B63", margin: "0 0 2px 0" }}>Sample notes</p>
              <p style={{ fontSize: 10.5, margin: 0, lineHeight: 1.4, whiteSpace: "pre-wrap" }}>{s.notes}</p>
            </div>
          )}

          {/* Scores table */}
          {(s.scores || []).length > 0 ? (
            <table className="scores">
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Cupper</th>
                  {SCORE_KEYS.map(([k, l]) => <th key={k}>{l}</th>)}
                  <th>Defects</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {s.scores.map((sc, j) => {
                  const total = SCORE_KEYS.reduce((acc, [k]) => acc + (sc[k] || 0), 0) - (sc.defects || 0);
                  return (
                    <tr key={j}>
                      <td className="cupper">{sc.user_name}</td>
                      {SCORE_KEYS.map(([k]) => <td key={k}>{sc[k] != null ? fmt(sc[k]) : "—"}</td>)}
                      <td style={{ color: "#b00" }}>{sc.defects || 0}</td>
                      <td style={{ fontWeight: 700 }}>{total.toFixed(2)}</td>
                    </tr>
                  );
                })}
                {s.scores.length > 1 && (
                  <tr className="avg-row">
                    <td className="cupper">Average</td>
                    {SCORE_KEYS.map(([k]) => {
                      const avg = s.scores.reduce((a, sc) => a + (sc[k] || 0), 0) / s.scores.length;
                      return <td key={k}>{fmt(avg)}</td>;
                    })}
                    <td>{fmt(s.scores.reduce((a, sc) => a + (sc.defects || 0), 0) / s.scores.length, 1)}</td>
                    <td>{s.avg_total != null ? fmt(s.avg_total) : "—"}</td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <p style={{ fontSize: 11, color: "#736B63", margin: 0 }}>No scores submitted.</p>
          )}

          {/* Per-cupper notes */}
          {(s.scores || []).some((sc) => sc.notes) && (
            <div style={{ marginTop: 8 }}>
              <p style={{ fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: "#736B63", margin: "0 0 4px 0" }}>Tasting notes</p>
              {s.scores.filter((sc) => sc.notes).map((sc, j) => (
                <p key={j} style={{ fontSize: 11, margin: "2px 0", lineHeight: 1.4 }}>
                  <strong>{sc.user_name}:</strong> {sc.notes}
                </p>
              ))}
            </div>
          )}

          {/* AI agtron / particle report */}
          {s.agtron_ai_report && (
            <div className="ai-report" style={{ marginTop: 10, background: "#fbf8f3", border: "1px solid #e6dfd2", borderRadius: 4, padding: 8 }}>
              <p style={{ fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: "#8C583E", margin: "0 0 4px 0", fontWeight: 700 }}>AI grind / particle report</p>
              <pre style={{ fontSize: 10.5, lineHeight: 1.5, margin: 0, whiteSpace: "pre-wrap", fontFamily: "Manrope, sans-serif" }}>
                {s.agtron_ai_report}
              </pre>
            </div>
          )}
        </div>
      ))}

      <p style={{ fontSize: 9, color: "#a39d95", textAlign: "center", marginTop: 24 }}>
        Generated {new Date().toLocaleString("en-ZA")} · Brothers Coffee Roastery
      </p>
    </div>
  );
}

function topSample(samples) {
  const winner = [...samples]
    .filter((s) => s.avg_overall)
    .sort((a, b) => (b.avg_overall || 0) - (a.avg_overall || 0))[0];
  if (!winner) return "—";
  return `${winner.label} (${fmt(winner.avg_overall)})`;
}

function Kpi({ label, value }) {
  return (
    <div style={{ background: "#f6f1e8", border: "1px solid #e6dfd2", borderRadius: 4, padding: 8 }}>
      <p style={{ fontSize: 9, letterSpacing: 1.2, textTransform: "uppercase", color: "#736B63", margin: 0 }}>{label}</p>
      <p style={{ fontSize: 18, fontWeight: 700, margin: "2px 0 0 0", fontVariantNumeric: "tabular-nums" }}>{value}</p>
    </div>
  );
}

function Mini({ label, value, accent }) {
  return (
    <div style={{ background: accent ? "#f4ddc8" : "#f6f1e8", border: `1px solid ${accent ? "#d4a878" : "#e6dfd2"}`, borderRadius: 4, padding: "4px 6px", textAlign: "center" }}>
      <p style={{ fontSize: 8, letterSpacing: 1, textTransform: "uppercase", color: "#736B63", margin: 0 }}>{label}</p>
      <p style={{ fontSize: 12, fontWeight: 700, margin: "2px 0 0 0", fontFamily: "JetBrains Mono, monospace" }}>{value}</p>
    </div>
  );
}
